import React, {useCallback, useState} from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Button,
  Stack,
  TextField,
  IconButton,
  Typography,
} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import DeleteIcon from '@mui/icons-material/Delete';
import {useStylesed} from '../../../components/styles/commonStyled';
import {DataGrid, GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import useDebounce from '../../../components/utlis/useDebounce';
import {ICompany} from '../../../dto/Company.dto';
import {apiDelete, apiPost} from '../../../axiosConfig';
import DialogApiRequestStatus from '../../../components/dialogs/DialogApiRequestStatus';
import {
  IGenericBaseType,
  useRqListExceptionCompanies,
  queryExceptionCompaniesList,
} from '../../../react_query/global-content/genericBaseService';
import DialogConfirmDeletion from '../../../components/dialogs/DialogConfirmDeletion';
import {UserCanAny} from '../../../components/UserCan';
import {useUserCanAny} from '../../../hooks/useUserCan';
import {useRqGetCompanies} from '../../../Service/companyService';
import {AxiosError} from 'axios';

// Utility to invalidate all download-management queries
export const invalidateDownloadManagementQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({
    predicate: query =>
      typeof query.queryKey?.[0] === 'string' &&
      query.queryKey[0].includes('download-management'),
    refetchType: 'active',
  });
};

export const getExceptionUrl = ({
  type,
  processType,
  partnerId,
  exceptionId,
}: {
  type: string;
  processType: number | string;
  partnerId?: number | string;
  exceptionId?: number | string;
}) => {
  switch (type) {
    case 'generic-stinger':
      return `v1/global-content/stingers/${processType}/exception${
        exceptionId ? '/' + exceptionId : ''
      }`;
    case 'download-management':
      return partnerId
        ? `v1/download-management/${partnerId}/exception`
        : `v1/download-management/exception`;
    default:
      return `v1/global-content/generic-base/type/${processType}/exception${
        exceptionId ? '/' + exceptionId : ''
      }`;
  }
};

export const handlePartnerExceptionCall = async ({
  partnerId,
  type,
  processType,
  setApiState,
  queryClient,
  inValidate = false,
}: {
  partnerId: number;
  type: string;
  processType: number;
  setApiState: React.Dispatch<
    React.SetStateAction<{status: string; error: unknown; message?: string}>
  >;
  queryClient: ReturnType<typeof useQueryClient>;
  inValidate: boolean;
}) => {
  const exceptionUrl = getExceptionUrl({
    type,
    processType,
    partnerId,
  });
  if (partnerId > 0) {
    setApiState({status: 'pending', error: null, message: ''});
    try {
      await apiPost(exceptionUrl, {
        company_id: partnerId,
        requestable_type: type,
      });
      setApiState({status: 'success', error: null, message: ''});
      if (inValidate) {
        invalidateDownloadManagementQueries(queryClient);
      }
    } catch (error) {
      setApiState({
        status: 'error',
        error,
        message: '',
      });
    }
  }
};

type Props = {
  genericBaseType: IGenericBaseType;
  type: string;
  inValidate?: boolean;
};

const getColumns = (type: string): GridColDef[] => {
  const baseColumns: GridColDef[] = [
    {
      field: 'company_name',
      headerName: 'Company Name',
      sortable: true,
      flex: 1,
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      sortable: true,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return params.row.created_by?.name ?? '';
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Action',
      hideable: false,
      sortable: false,
      width: 80,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <ActiveBtn
            params={params}
            inValidate={type === 'download-management'}
          />
        );
      },
    },
  ];
  const filtered =
    type === 'download-management'
      ? baseColumns.filter(col => col.field !== 'created_by')
      : baseColumns;
  return filtered.map(column => ({
    ...column,
    filterable: false,
  }));
};

export default function GlobalContentExceptionCompanies({
  type,
  genericBaseType,
  inValidate = false,
}: Props) {
  const processType = genericBaseType.id;
  const product_type = genericBaseType.product_id;
  const classes = useStylesed();
  const queryClient = useQueryClient();
  const [currentPartner, setCurrentPartner] = React.useState<ICompany>({
    id: 0,
    name: '',
  });
  const [apiState, setApiState] = useState<{
    status: string;
    error: AxiosError | null;
    message?: string;
  }>({
    status: '',
    error: null,
    message: '',
  });

  const [partnerSearch, setPartnerSearch] = React.useState<string | null>('');
  const debouncedSearch = useDebounce(partnerSearch, 500);
  const {data: companyList, isFetching: isCompanyLoading} = useRqGetCompanies(
    {
      search: debouncedSearch || '',
      product_id: product_type ? [product_type] : [],
      type:
        genericBaseType.name === 'download-management'
          ? 'download-management'
          : '',
    },
    !!debouncedSearch,
  );
  const canEdit = useUserCanAny(
    'is-admin,generic-base-company-exception:destroy',
  );

  const selectOnchange = useCallback((value: ICompany) => {
    setCurrentPartner(value);
  }, []);

  const {data: exceptionData, isFetching: isLoading} =
    useRqListExceptionCompanies(processType, type);

  const getRowClassName = React.useCallback(() => {
    return classes.cls_active;
  }, [classes.cls_active]);

  const columns = getColumns(type);
  const handleSave = useCallback(() => {
    handlePartnerExceptionCall({
      partnerId: currentPartner.id,
      type: type,
      processType: processType,
      setApiState: setApiState as React.Dispatch<
        React.SetStateAction<{
          status: string;
          error: unknown;
          message?: string;
        }>
      >,
      queryClient,
      inValidate,
    });
  }, [currentPartner.id, processType, queryClient, type, inValidate]);

  React.useEffect(() => {
    if (processType > 0) {
      setCurrentPartner({id: 0, name: ''});
    }
  }, [processType]);

  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null, message: ''});
  }, []);

  const handleClose = useCallback(() => {
    setApiState({status: '', error: null, message: ''});
    setCurrentPartner({id: 0, name: ''});
    queryClient.invalidateQueries({
      queryKey: [queryExceptionCompaniesList(genericBaseType.id, type)],
      refetchType: 'active',
    });
  }, [genericBaseType, queryClient, type]);

  return (
    <>
      <Box
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          flexDirection: 'column',
          width: '100%',
        }}>
        {type !== 'download-management' && (
          <Typography variant='h4'>Exclusion List</Typography>
        )}
        <Stack direction='row' spacing={1}>
          <Autocomplete
            fullWidth
            sx={{width: '100%'}}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return option === value;
              return option.name === value.name;
            }}
            value={currentPartner?.id ? currentPartner : null}
            getOptionLabel={(option: ICompany) =>
              option?.name !== undefined ? option.name : ''
            }
            noOptionsText={'Exclude Partner...'}
            options={companyList ?? []}
            onChange={(
              _: React.SyntheticEvent<Element, Event>,
              newValue: ICompany | null,
            ) => {
              setPartnerSearch(newValue?.name ?? '');
              selectOnchange(newValue ?? {id: 0, name: ''});
            }}
            onInputChange={(event, newInputValue) => {
              if (event && event.type === 'change') {
                setPartnerSearch(newInputValue ?? '');
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                label='Exclude Partner'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {isCompanyLoading ? (
                        <CircularProgress color='inherit' size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Stack>

        {currentPartner && currentPartner.id > 0 ? (
          <UserCanAny scope='is-admin,generic-base-company-exception:store'>
            <Button
              sx={{width: '120px', marginLeft: 'auto'}}
              variant='contained'
              color='warning'
              onClick={handleSave}>
              Add
            </Button>
          </UserCanAny>
        ) : null}
      </Box>

      <Box sx={{height: 'calc(100vh - 265px)'}} p={1}>
        <DataGrid
          disableColumnSelector
          rows={exceptionData ?? []}
          columns={columns}
          loading={isLoading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          getRowClassName={getRowClassName}
          pageSizeOptions={[10, 20, 50, 75, 100]}
          disableRowSelectionOnClick
          slotProps={{
            columnsPanel: {
              disableHideAllButton: true,
              disableShowAllButton: true,
            },
          }}
          columnVisibilityModel={{
            actions: canEdit,
          }}
          getRowHeight={() => 'auto'}
        />
      </Box>

      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleSave}
          onEdit={handleEdit}
          onClose={() => handleClose()}
        />
      )}
    </>
  );
}

const ActiveBtn = ({
  params,
  inValidate,
}: {
  params: GridRenderCellParams;
  inValidate: boolean;
}) => {
  const typeID = params.row.requestable_id;
  const requestType = params.row.requestable_type;
  const exceptionId = params.row.id;
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [apiState, setApiState] = useState({
    status: '',
    error: null,
    successMsg: '',
  });
  const queryClient = useQueryClient();

  const handleDelete = () => {
    setApiState({status: 'pending', error: null, successMsg: ''});
    const deleteUrl = getExceptionUrl({
      type: requestType,
      processType: typeID,
      partnerId: typeID,
      exceptionId,
    });
    apiDelete(deleteUrl)
      .then(() => {
        setApiState({status: 'success', error: null, successMsg: ''});
        setIsDelete(false);
        if (inValidate) {
          invalidateDownloadManagementQueries(queryClient);
        }
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
          successMsg: '',
        });
      });
  };

  const handleRemove = () => {
    setIsDelete(true);
  };
  const handleCancel = () => {
    setIsDelete(false);
  };
  const handleActiveBtnEdit = React.useCallback(() => {
    setApiState({status: '', error: null, successMsg: ''});
  }, []);

  const handleActiveBtnClose = useCallback(() => {
    setApiState({status: '', error: null, successMsg: ''});
    queryClient.invalidateQueries({
      queryKey: [queryExceptionCompaniesList(typeID, requestType)],
      refetchType: 'active',
    });
  }, [queryClient, typeID, requestType]);
  if (apiState.status !== '') {
    return (
      <DialogApiRequestStatus
        apiState={apiState}
        onRetry={handleDelete}
        onEdit={handleActiveBtnEdit}
        onClose={handleActiveBtnClose}
      />
    );
  }
  if (isDelete) {
    return (
      <DialogConfirmDeletion
        name={<span>from Exception Companies</span>}
        onDelete={handleDelete}
        onCancel={handleCancel}
        isPermanent
      />
    );
  }

  return (
    <IconButton color='error' onClick={handleRemove}>
      <DeleteIcon />
    </IconButton>
  );
};
